import { TextareaHTMLAttributes, useCallback, useRef } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  COLOR_PRIMARY,
  COLOR_LIGHT_RED,
  COLOR_RED,
} from "../../constants/color";
import { CloseIcon } from "./icons/CloseIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";

type Props = {
  name: string;
  uuid?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: string;
  clearable?: boolean;
  fullWidth?: boolean;
  maxLength?: number;
  rows?: number;
  className?: string;
};

export const Textarea: React.FC<Props> = ({
  name,
  uuid,
  label,
  placeholder,
  defaultValue,
  value,
  onChange,
  required,
  disabled,
  autoFocus,
  hasError,
  errorMessage,
  helperText,
  clearable,
  fullWidth,
  maxLength,
  rows = 10,
  className,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formContext = useFormContext();
  const { register } = formContext ?? {};

  const handleOnClear = useCallback(() => {
    if (onChange) onChange("");
    if (formContext) formContext.setValue(name, "", { shouldValidate: true });

    if (!textareaRef.current) return;

    textareaRef.current.value = "";
  }, [formContext, textareaRef, onChange, name]);

  return (
    <StyledContainer $fullWidth={fullWidth} className={className}>
      <StyledHeader>
        {label && (
          <StyledLabel>
            {required ? (
              <>
                {label.replace(' *', '')} <StyledRequiredAsterisk>*</StyledRequiredAsterisk>
              </>
            ) : (
              label
            )}
          </StyledLabel>
        )}
      </StyledHeader>

      <StyledTextareaContainer>
        <StyledTextarea
          id={uuid ? `textarea-${uuid}` : undefined}
          {...register?.(name)}
          ref={register?.(name).ref ?? textareaRef}
          onChange={(e) => {
            if (onChange) onChange(e.target.value);
            register?.(name).onChange(e);
          }}
          placeholder={placeholder}
          defaultValue={defaultValue}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          rows={rows}
          autoFocus={autoFocus}
          $hasError={hasError}
          $clearable={clearable}
          onInput={(e) => {
            if (maxLength) {
              e.currentTarget.value = e.currentTarget.value.slice(0, maxLength);
            }
          }}
        />

        {clearable && (
          <StyledClearIcon
            id={`textarea-clear-icon-${uuid}`}
            onClick={handleOnClear}
          >
            <CloseIcon uuid={uuid} />
          </StyledClearIcon>
        )}
      </StyledTextareaContainer>

      {helperText && (
        <StyledHelperTextContainer $hasError={hasError}>
          <StyledHelperTextIconContainer>※</StyledHelperTextIconContainer>
          <StyledHelperTextMessage>{helperText}</StyledHelperTextMessage>
        </StyledHelperTextContainer>
      )}

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledRequiredAsterisk = styled.span`
  color: ${COLOR_RED};
`;

const StyledTextareaContainer = styled.div`
  position: relative;
`;

const StyledTextarea = styled.textarea<{
  $hasError?: boolean;
  $clearable?: boolean;
}>`
  @media (max-width: 990px) {
    font-size: 16px;
  }
  
  width: 100%;
  padding: 17px 16px;
  ${({ $clearable }) => $clearable && 'padding-right: 48px;'}
  border-radius: 8px;
  border: 1px solid #cccccc;
  font-size: 14px;
  line-height: 150%;
  outline: none;
  background-color: #ffffff;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: #999999;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
      border-color: ${COLOR_RED};
      background-color: ${COLOR_LIGHT_RED};
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
      $hasError ? COLOR_RED : COLOR_PRIMARY};
  }

  &:disabled {
    background-color: #f9f9f9;
    border-color: #cccccc;
    color: #333333;
    cursor: not-allowed;
    resize: none;

    &::placeholder {
      color: #999999;
    }
  }
`;

const StyledClearIcon = styled.div`
  position: absolute;
  top: 17px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  outline: none;
  
  &:focus {
    outline: none;
  }
`;

const StyledHelperTextContainer = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  ${({ $hasError }) => $hasError && `color: ${COLOR_RED};`}
`;

const StyledHelperTextIconContainer = styled.div`
  margin-right: 10px;
`;

const StyledHelperTextMessage = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${COLOR_RED};
`; 